import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";

import api from "./../../../services/api";

import "./styles.css";
import {
  Container,
  RatingContainer,
  RatingBox,
  RatingTitle,
  RatingStars,
  StarButton,
  RatingComment,
  RatingTextarea,
  RatingButton,
  RatingMessage,
} from "./styles.js";

import { parseISO, eachDayOfInterval, format, isSameDay } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import pt from "date-fns/locale/pt-BR";

function getZonedDate(date) {
  if (!date) return null;
  return utcToZonedTime(typeof date === "string" ? parseISO(date) : date, "");
}

function getCourseDates(course) {
  const start = getZonedDate(course?.start_date);
  const end = getZonedDate(course?.end_date || course?.start_date);

  if (!start) {
    return { labels: [], datesText: "", timeText: "", singleDay: true };
  }

  const days = eachDayOfInterval({ start, end: end || start });
  const labels = days.map(day => format(day, "dd/MM/yyyy", { locale: pt }));
  const singleDay = days.length === 1 || isSameDay(start, end || start);

  let datesText = "";
  if (labels.length === 1) {
    datesText = `no dia ${labels[0]}`;
  } else if (labels.length === 2) {
    datesText = `nos dias ${labels[0]} e ${labels[1]}`;
  } else {
    datesText = `nos dias ${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`;
  }

  const timeText = `das ${format(start, "HH:mm")} às ${format(end || start, "HH:mm")} horas`;

  return { labels, datesText, timeText, singleDay };
}

function getInstructorsText(course) {
  if (course?.speakers && course.speakers.length) {
    return course.speakers.map(s => s.name).join(", ");
  }
  return course?.instructors || "";
}

function Print() {
  let { configReducers: config } = useSelector(state => state);
  config = config.config;

  const { key: keyParam } = useParams();
  const key = keyParam ? String(keyParam).trim().toUpperCase() : "";
  const history = useHistory();

  const [certificate, setCertificate] = useState({});
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [hasRating, setHasRating] = useState(false);
  const [showRatingForm, setShowRatingForm] = useState(false); // Inicia como false até verificar
  const [loading, setLoading] = useState(false);
  const [ratingMessage, setRatingMessage] = useState("");
  const [checkingRating, setCheckingRating] = useState(true);

  useEffect(() => {
    async function loadCertificate() {
      if (!key) {
        alert("Erro ao processar certificado.");
        history.goBack();
        return;
      }

      // Primeiro carrega o certificado (obrigatório)
      try {
        const { data } = await api.get(`/courses/check/certificate/?key=${encodeURIComponent(key)}`);
        setCertificate(data);

        // Verifica se o certificado já tem rating
        if (data.rating) {
          setHasRating(true);
          setShowRatingForm(false);
          setRating(data.rating.rating);
          setComment(data.rating.comment || "");
        } else {
          // Se não tem rating no certificado, verifica no endpoint específico (opcional)
          try {
            const ratingCheck = await api.get(`/courses/check/rating/?key=${encodeURIComponent(key)}`);
            if (ratingCheck.data.hasRating) {
              setHasRating(true);
              setShowRatingForm(false);
              setRating(ratingCheck.data.rating?.rating || 0);
              setComment(ratingCheck.data.rating?.comment || "");

              window.print();
            } else {
              // Não existe avaliação, pode mostrar formulário
              setShowRatingForm(true);
            }
          } catch (ratingErr) {
            setShowRatingForm(true);
          }
        }

        setCheckingRating(false);
      } catch (err) {
        console.error("Erro ao carregar certificado:", err);
        if (err.response?.status === 404) {
          alert("Não foi possivel emitir o certificado.\nCertificado não existe.");
          history.goBack();
        } else {
          alert("Erro ao carregar certificado. Tente novamente.");
          history.goBack();
        }
        setCheckingRating(false);
      }
    }

    loadCertificate();
  }, [key, history]);

  const handleStarClick = value => {
    if (!hasRating) {
      setRating(value);
    }
  };

  const handleSubmitRating = async e => {
    e.preventDefault();

    // Verificação adicional antes de enviar
    if (hasRating) {
      alert("Este curso já foi avaliado.");
      setShowRatingForm(false);
      return;
    }

    if (rating === 0) {
      alert("Por favor, selecione uma nota para o curso.");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/courses/save/rating/?key=${encodeURIComponent(key)}`, {
        rating,
        comment: comment.trim() || null,
      });

      // Marca como avaliado imediatamente para evitar duplo envio
      setHasRating(true);
      setShowRatingForm(false);
      setRatingMessage("Obrigado pela sua avaliação!");

      // Aguardar um pouco antes de imprimir
      setTimeout(() => {
        if (typeof global.window !== "undefined") {
          window.print();
        }
      }, 1500);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        alert("Este curso já foi avaliado. Você só pode avaliar uma vez.");
        setHasRating(true);
        setShowRatingForm(false);
        // Recarrega para garantir sincronização
        try {
          const { data } = await api.get(`/courses/check/certificate/?key=${encodeURIComponent(key)}`);
          if (data.rating) {
            setRating(data.rating.rating);
            setComment(data.rating.comment || "");
          }
        } catch (reloadErr) {
          console.error("Erro ao recarregar:", reloadErr);
        }
      } else {
        alert("Erro ao salvar avaliação. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkipRating = () => {
    setShowRatingForm(false);
    // setTimeout(() => {
    //   if (typeof global.window !== "undefined") {
    //     window.print();
    //   }
    // }, 500);
  };

  React.useEffect(() => {
    const hideChat = () => {
      if (typeof window !== "undefined") {
        const chatIframe = document.querySelector("#iframe-sz-chat");
        if (chatIframe) {
          chatIframe.style.opacity = "0";
        }
      }
    };

    hideChat();

    // Adiciona listeners para garantir que o iframe seja ocultado após o carregamento completo
    if (typeof window !== "undefined") {
      window.addEventListener("load", hideChat);
      return () => window.removeEventListener("load", hideChat);
    }
  }, []);

  // useEffect(() => {
  //   // Só imprime automaticamente se não houver formulário de avaliação para mostrar
  //   if (!showRatingForm && !hasRating && typeof global.window !== "undefined") {
  //     setTimeout(() => {
  //       window.print();
  //     }, 2000);
  //   } else if (hasRating && typeof global.window !== "undefined") {
  //     // Se já tem avaliação, imprime após carregar
  //     setTimeout(() => {
  //       window.print();
  //     }, 2000);
  //   }
  // }, [showRatingForm, hasRating]);

  const course = certificate?.course || {};
  const courseDates = getCourseDates(course);
  const instructorsText = getInstructorsText(course);
  const speakers = course?.speakers || [];

  return (
    <>
      {checkingRating && (
        <RatingContainer>
          <RatingBox>
            <RatingTitle>Carregando...</RatingTitle>
          </RatingBox>
        </RatingContainer>
      )}

      {!checkingRating && showRatingForm && !hasRating && (
        <RatingContainer>
          <RatingBox>
            <RatingTitle>Avalie este curso</RatingTitle>
            <form onSubmit={handleSubmitRating}>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "10px", fontFamily: "NunitoSans-SemiBold", fontSize: "16px", color: "#3c3535" }}>
                  Como você avalia este curso? *
                </label>
                <RatingStars>
                  {[1, 2, 3, 4, 5].map(value => (
                    <StarButton key={value} type="button" onClick={() => handleStarClick(value)} selected={rating >= value} disabled={hasRating}>
                      ★
                    </StarButton>
                  ))}
                </RatingStars>
              </div>

              <RatingComment>
                <label style={{ display: "block", marginBottom: "10px", fontFamily: "NunitoSans-SemiBold", fontSize: "16px", color: "#3c3535" }}>
                  Comentário (opcional)
                </label>
                <RatingTextarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Deixe seu comentário sobre o curso..."
                  disabled={hasRating}
                  rows={4}
                />
              </RatingComment>

              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <RatingButton type="submit" disabled={loading || rating === 0 || hasRating}>
                  {loading ? "Salvando..." : hasRating ? "Já Avaliado" : "Enviar Avaliação"}
                </RatingButton>
                {/* <RatingButton type="button" onClick={handleSkipRating} style={{ backgroundColor: "#9B9B9B" }} disabled={loading || hasRating}>
                  Pular
                </RatingButton> */}
              </div>
            </form>
          </RatingBox>
        </RatingContainer>
      )}

      {ratingMessage && <RatingMessage>{ratingMessage}</RatingMessage>}

      <Container>
        <div className="certificate certificate-page">
          <img src={course?.certificate_path} alt="" />
          <div className="content">
            Certificamos que o(a) <strong>{certificate?.user?.name}</strong>, inscrito(a) sob CPF nº <strong>{certificate?.user?.cpf}</strong> participou do
            curso <strong className="courseName">{course?.name}</strong>
            {instructorsText ? (
              <>
                , ministrado(a) pelo(a) professor(a) <strong>{instructorsText}</strong>
              </>
            ) : null}
            , realizado {courseDates.datesText}, com a carga horária de <strong>{course?.total_hours}</strong> hora(s), {courseDates.timeText}
            {course?.local ? (
              <>
                , realizado no(a) <strong>{course.local}</strong>
              </>
            ) : null}
            .
          </div>
          {key ? (
            <div className="validation">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
                  `https://cromt.org.br/validar-certificado/${key}`,
                )}`}
                alt="QR Code de validação"
              />
              <span>Validar: {key}</span>
            </div>
          ) : null}
        </div>

        <div className="certificate certificate-page certificate-details">
          <div className="details-content">
            <h1>Informações complementares do curso</h1>
            <h2>{course?.name}</h2>

            {course?.summary ? (
              <section className="details-section">
                <h3>Sobre o curso</h3>
                <div className="details-text" dangerouslySetInnerHTML={{ __html: course.summary }} />
              </section>
            ) : null}

            <section className="details-section">
              <h3>Período de realização</h3>
              <ul className="details-list">
                {courseDates.labels.map(dateLabel => (
                  <li key={dateLabel}>{dateLabel}</li>
                ))}
              </ul>
              <p className="details-text">
                Horário: {courseDates.timeText}
                {course?.total_hours ? ` | Carga horária total: ${course.total_hours} hora(s)` : ""}
              </p>
            </section>

            {(course?.local || course?.address) && (
              <section className="details-section">
                <h3>Local</h3>
                {course?.local ? <p className="details-text">{course.local}</p> : null}
                {course?.address ? <p className="details-text">{course.address}</p> : null}
                {course?.is_virtual ? <p className="details-text">Modalidade: Digital/Online/EAD</p> : null}
              </section>
            )}

            {speakers.length > 0 ? (
              <section className="details-section">
                <h3>Palestrante(s)</h3>
                <div className="speakers-grid">
                  {speakers.map(speaker => (
                    <div className="speaker-item" key={speaker.id}>
                      {speaker.image_path ? (
                        <img className="speaker-photo" src={speaker.image_path} alt={speaker.name} />
                      ) : null}
                      <div className="speaker-info">
                        <strong>{speaker.name}</strong>
                        {speaker.education ? <p>{speaker.education}</p> : null}
                        {speaker.curriculum ? (
                          <div
                            className="speaker-curriculum"
                            dangerouslySetInnerHTML={{
                              __html: String(speaker.curriculum).replace(/\n/g, "<br />"),
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : instructorsText || course?.curriculum ? (
              <section className="details-section">
                <h3>Palestrante(s)</h3>
                {instructorsText ? <p className="details-text">{instructorsText}</p> : null}
                {course?.curriculum ? (
                  <div className="details-text" dangerouslySetInnerHTML={{ __html: course.curriculum }} />
                ) : null}
              </section>
            ) : null}

            {key ? (
              <section className="details-section details-validation">
                <h3>Autenticidade</h3>
                <p className="details-text">
                  Código de validação: <strong>{key}</strong>
                </p>
                <p className="details-text">Consulte em cromt.org.br/validar-certificado</p>
              </section>
            ) : null}
          </div>
        </div>
      </Container>
    </>
  );
}

export default Print;
